export type ProjectTypeId = "custom-software" | "web-app" | "mobile-app" | "saas" | "integration" | "automation";
export type PlatformTypeId = "web" | "mobile" | "hybrid";
export type FeatureTypeId = "auth-rbac" | "dashboard" | "vault" | "scheduler" | "billing" | "multi-tenant";
export type IntegrationTypeId = "whatsapp" | "crm-erp" | "custom-api";
export type AiTypeId = "none" | "agent-rag";
export type InfrastructureTypeId = "basic" | "enterprise";

export interface ProjectTypeConfig {
  id: ProjectTypeId;
  label: string;
  description: string;
  points: number;
  baseCost: number; // PLANNING_PLACEHOLDER
  timelineWeeks: { min: number; max: number };
}

export interface PlatformConfig {
  id: PlatformTypeId;
  label: string;
  description: string;
  points: number;
  multiplier: number; // PLANNING_PLACEHOLDER
}

export interface FeatureConfig {
  id: FeatureTypeId;
  label: string;
  description: string;
  points: number;
  modifierCost: number; // PLANNING_PLACEHOLDER
  timelineWeeks: number;
}

export interface IntegrationConfig {
  id: IntegrationTypeId;
  label: string;
  description: string;
  points: number;
  modifierCost: number; // PLANNING_PLACEHOLDER
  timelineWeeks: number;
}

export interface AiConfig {
  id: AiTypeId;
  label: string;
  description: string;
  points: number;
  modifierCost: number; // PLANNING_PLACEHOLDER
  timelineWeeks: number;
}

export interface InfrastructureConfig {
  id: InfrastructureTypeId;
  label: string;
  description: string;
  points: number;
  modifierCost: number; // PLANNING_PLACEHOLDER
  timelineWeeks: number;
}

export interface ComplexityTier {
  id: "low" | "moderate" | "high" | "enterprise";
  label: string;
  description: string;
  minPoints: number;
  maxPoints: number;
  timelineWeeks: { min: number; max: number };
}

// --------------------------------------------------
// CONFIGURATION VALUES (PLANNING_PLACEHOLDERS)
// TODO: These values require B2B business approval before estimator launch
// --------------------------------------------------

export const projectTypes: ProjectTypeConfig[] = [
  {
    id: "custom-software",
    label: "Custom Business System",
    description: "Custom admin portals replacing manual spreadsheets and custom workflow engines.",
    points: 10,
    baseCost: 200000, // PLANNING_PLACEHOLDER
    timelineWeeks: { min: 6, max: 10 }
  },
  {
    id: "web-app",
    label: "Web Application Portal",
    description: "Low-latency user dashboards, data grids, and client booking interfaces.",
    points: 12,
    baseCost: 150000, // PLANNING_PLACEHOLDER
    timelineWeeks: { min: 6, max: 8 }
  },
  {
    id: "mobile-app",
    label: "Mobile Application",
    description: "Cross-platform iOS and Android apps with SQLite local caching and offline triggers.",
    points: 15,
    baseCost: 250000, // PLANNING_PLACEHOLDER
    timelineWeeks: { min: 8, max: 12 }
  },
  {
    id: "saas",
    label: "B2B SaaS Product",
    description: "Multi-tenant products featuring isolated datastores and Stripe subscriptions.",
    points: 18,
    baseCost: 300000, // PLANNING_PLACEHOLDER
    timelineWeeks: { min: 10, max: 16 }
  },
  {
    id: "integration",
    label: "CRM / ERP Integration",
    description: "Bi-directional ledger synchronization linking Salesforce, HubSpot, and custom databases.",
    points: 8,
    baseCost: 100000, // PLANNING_PLACEHOLDER
    timelineWeeks: { min: 4, max: 8 }
  },
  {
    id: "automation",
    label: "WhatsApp / API Automation",
    description: "High-speed webhook integrations triggered from status ledgers.",
    points: 6,
    baseCost: 80000, // PLANNING_PLACEHOLDER
    timelineWeeks: { min: 4, max: 6 }
  }
];

export const platforms: PlatformConfig[] = [
  {
    id: "web",
    label: "Web Only",
    description: "Deploy to modern web browsers.",
    points: 2,
    multiplier: 1.0 // PLANNING_PLACEHOLDER
  },
  {
    id: "mobile",
    label: "Mobile Only",
    description: "Native mobile build targeting iOS & Android stores.",
    points: 4,
    multiplier: 1.2 // PLANNING_PLACEHOLDER
  },
  {
    id: "hybrid",
    label: "Web + Mobile (Hybrid)",
    description: "Deploy web console alongside companion app wrappers.",
    points: 8,
    multiplier: 1.6 // PLANNING_PLACEHOLDER
  }
];

export const features: FeatureConfig[] = [
  {
    id: "auth-rbac",
    label: "Secure Logins & Roles (RBAC)",
    description: "Encrypted JWT auth with granular admin/staff view routing permissions.",
    points: 4,
    modifierCost: 40000, // PLANNING_PLACEHOLDER
    timelineWeeks: 1
  },
  {
    id: "dashboard",
    label: "Reporting & Charts Console",
    description: "Interactive data charts showing transactional operations status.",
    points: 5,
    modifierCost: 45000, // PLANNING_PLACEHOLDER
    timelineWeeks: 2
  },
  {
    id: "vault",
    label: "Cloud Document Vault",
    description: "Secure storage vaults for landlord documents or logistics invoices.",
    points: 5,
    modifierCost: 50000, // PLANNING_PLACEHOLDER
    timelineWeeks: 2
  },
  {
    id: "scheduler",
    label: "Calendar Booking Engine",
    description: "Automated schedule planners with real-time API sync and conflict checking.",
    points: 6,
    modifierCost: 55000, // PLANNING_PLACEHOLDER
    timelineWeeks: 2
  },
  {
    id: "billing",
    label: "Subscription Billing (Stripe)",
    description: "Stripe recurring paywall integration and invoice status hooks.",
    points: 5,
    modifierCost: 50000, // PLANNING_PLACEHOLDER
    timelineWeeks: 2
  },
  {
    id: "multi-tenant",
    label: "Multi-Tenant Isolation",
    description: "Tenant isolation schemas protecting company datastores from exposure.",
    points: 8,
    modifierCost: 80000, // PLANNING_PLACEHOLDER
    timelineWeeks: 3
  }
];

export const integrations: IntegrationConfig[] = [
  {
    id: "whatsapp",
    label: "WhatsApp Cloud API Webhooks",
    description: "Programmatic notification webhooks triggering driver dispatch alerts.",
    points: 4,
    modifierCost: 35000, // PLANNING_PLACEHOLDER
    timelineWeeks: 1
  },
  {
    id: "crm-erp",
    label: "Enterprise CRM / ERP Sync",
    description: "API connectors coordinating external sales leads with local databases.",
    points: 6,
    modifierCost: 45000, // PLANNING_PLACEHOLDER
    timelineWeeks: 2
  },
  {
    id: "custom-api",
    label: "Custom API Connectors",
    description: "Dedicated hooks syncing records with third-party proprietary servers.",
    points: 5,
    modifierCost: 40000, // PLANNING_PLACEHOLDER
    timelineWeeks: 2
  }
];

export const aiComplexity: AiConfig[] = [
  {
    id: "none",
    label: "No AI Integrations",
    description: "Traditional rules-based scheduling databases.",
    points: 0,
    modifierCost: 0,
    timelineWeeks: 0
  },
  {
    id: "agent-rag",
    label: "LLM Agents & RAG Pipelines",
    description: "Embedding custom text, vector store index, and RAG pipelines for search context.",
    points: 12,
    modifierCost: 120000, // PLANNING_PLACEHOLDER
    timelineWeeks: 4
  }
];

export const infrastructure: InfrastructureConfig[] = [
  {
    id: "basic",
    label: "Basic Cloud Hosting",
    description: "Standard secure server deployment.",
    points: 2,
    modifierCost: 15000, // PLANNING_PLACEHOLDER
    timelineWeeks: 1
  },
  {
    id: "enterprise",
    label: "High-Availability Devops Container",
    description: "Docker containment pipelines, Terraform IaC blueprints, and TLS 1.3 compliance.",
    points: 8,
    modifierCost: 60000, // PLANNING_PLACEHOLDER
    timelineWeeks: 3
  }
];

// Complexity Tiers mapped deterministically based on points total
export const complexityTiers: ComplexityTier[] = [
  {
    id: "low",
    label: "Low Complexity",
    description: "Standard workflow application with simple database tables and clean admin layouts.",
    minPoints: 0,
    maxPoints: 20,
    timelineWeeks: { min: 4, max: 6 }
  },
  {
    id: "moderate",
    label: "Moderate Complexity",
    description: "Multi-user business portal featuring API syncs and automated trigger notifications.",
    minPoints: 21,
    maxPoints: 40,
    timelineWeeks: { min: 6, max: 10 }
  },
  {
    id: "high",
    label: "High Complexity",
    description: "Comprehensive software builds with complex database schemas and multi-role operations.",
    minPoints: 41,
    maxPoints: 60,
    timelineWeeks: { min: 10, max: 16 }
  },
  {
    id: "enterprise",
    label: "Enterprise Scale",
    description: "Multi-tenant cloud configurations, LLM vectors, and advanced security audits.",
    minPoints: 61,
    maxPoints: 999,
    timelineWeeks: { min: 16, max: 24 }
  }
];

// --------------------------------------------------
// BUSINESS RULES & ESTIMATOR CONFIG
// --------------------------------------------------

export const planningRangeMultiplier = {
  min: 0.90, // PLANNING_PLACEHOLDER: Calculated Cost x 0.90
  max: 1.15  // PLANNING_PLACEHOLDER: Calculated Cost x 1.15
};

export const disclaimers = {
  main: "This is a preliminary planning estimate based on typical scoping complexity, not a final commercial quotation. Scoping blueprints are finalized during a technology consultation.",
  uiShort: "Preliminary planning estimate. Official quotations are mapped in scoping workshops."
};

export const businessRules = {
  minCustomSoftwareThreshold: 100000, // PLANNING_PLACEHOLDER (Minimum project scale start ₹1,00,000)
  currency: "INR",
  currencySymbol: "₹",
  supportUSD: false, // Planned for future currency toggle iterations
  leadCaptureRequired: true, // Requires B2B contact details to unlock cost ranges
  notificationEmailPlaceholder: "CONTACT_EMAIL" // Maps to SMTP process.env notifications destination
};

// Canonical Event Constants for Analytics
export const analyticsEvents = {
  estimatorStarted: "estimator_started",
  estimatorStepCompleted: "estimator_step_completed",
  estimatorCompleted: "estimator_completed",
  estimatorLeadSubmitted: "estimator_lead_submitted",
  estimatorAbandoned: "estimator_abandoned"
};
